import type { Task } from './taskEngine';

export const householdTasks: Task[] = [
  {
    id: 'guest-solo-quick',
    roomId: 'middle-hall',
    title: 'Gör plats för gäster',
    category: 'visible',
    durationMinutes: 10,
    effort: 1,
    tools: ['Korg eller låda'],
    steps: ['Öppna ett eller två fönster en kort stund.', 'Samla allt löst från entrén och sällskapsytan i en korg.', 'Ställ undan korgen och frigör sittplatserna.'],
    doneDefinition: ['Det går att komma in och sätta sig utan att först flytta saker.'],
    modes: ['guests'],
    childFriendly: false,
    depth: 'quick',
    maximumParticipants: 1,
    repeatGroup: 'guest-solo-sweep',
    seasonalVariants: {
      winter: { steps: ['Tvärvädra i ungefär 2–4 minuter.', 'Samla allt löst från entrén och sällskapsytan i en korg.', 'Ställ undan korgen och frigör sittplatserna.'] },
      summer: { steps: ['Öppna ett par säkra fönster medan du gör resten av svepet, ungefär 10 minuter.', 'Samla allt löst från entrén och sällskapsytan i en korg.', 'Ställ undan korgen och frigör sittplatserna.'] },
    },
  },
  {
    id: 'guest-solo-sweep',
    roomId: 'middle-hall',
    title: 'Gör ett snabbt gästsvep',
    category: 'visible',
    durationMinutes: 15,
    effort: 2,
    tools: ['Korg eller låda'],
    steps: ['Öppna ett eller två fönster en kort stund.', 'Ta en korg genom entrén och ytan där ni ska vara; lägg allt löst i den.', 'Ställ undan korgen, frigör sittplatser och lägg fram en ren handduk i badrummet.'],
    doneDefinition: ['Det går att komma in, sätta sig och använda badrummet utan att först flytta saker.'],
    modes: ['guests'],
    childFriendly: false,
    depth: 'quick',
    maximumParticipants: 1,
    minimumSessionMinutes: 15,
    repeatGroup: 'guest-solo-sweep',
    supersedesTaskIds: ['guest-solo-quick'],
    seasonalVariants: {
      winter: { steps: ['Tvärvädra i ungefär 2–4 minuter.', 'Ta en korg genom entrén och ytan där ni ska vara; lägg allt löst i den.', 'Ställ undan korgen, frigör sittplatser och lägg fram en ren handduk i badrummet.'] },
      summer: { steps: ['Öppna ett par säkra fönster medan du gör resten av svepet, ungefär 10–15 minuter.', 'Ta en korg genom entrén och ytan där ni ska vara; lägg allt löst i den.', 'Ställ undan korgen, frigör sittplatser och lägg fram en ren handduk i badrummet.'] },
    },
  },
  {
    id: 'guest-entry-reset',
    roomId: 'middle-hall',
    title: 'Gör entrén välkomnande',
    category: 'visible',
    durationMinutes: 4,
    effort: 1,
    tools: ['Korg eller låda'],
    steps: ['Frigör vägen in genom hallen.', 'Häng upp ytterkläder och ställ skor parvis.', 'Samla småsaker i en korg för senare sortering.'],
    doneDefinition: ['Gäster kan komma in och hänga av sig utan att flytta saker.'],
    modes: ['guests'],
    childFriendly: true,
    depth: 'quick',
    minimumParticipants: 2,
  },
  {
    id: 'guest-bathroom-reset',
    roomId: 'upstairs-bathroom',
    title: 'Gör gästbadrummet redo',
    category: 'hygiene',
    durationMinutes: 5,
    effort: 1,
    tools: ['Mikrofiberduk', 'Toalettborste'],
    products: ['Badrumsrengöring'],
    steps: ['Torka handfat, kran och toalettens synliga ytor.', 'Ta bort synliga märken från spegeln.', 'Lägg fram en ren handduk och kontrollera toalettpapper.'],
    doneDefinition: ['Handfat och toalett känns rena och det finns handduk och papper.'],
    modes: ['guests'],
    childFriendly: true,
    depth: 'quick',
    minimumParticipants: 2,
  },
  {
    id: 'guest-living-room-reset',
    roomId: 'living-room',
    title: 'Landa ytan där ni ska vara',
    category: 'visible',
    durationMinutes: 5,
    effort: 1,
    tools: ['Korg eller låda'],
    steps: ['Frigör sittplatser och den yta ni ska använda.', 'Samla lösa saker i en korg.', 'Rätta till kuddar och filtar.'],
    doneDefinition: ['Gäster kan sätta sig och ställa ifrån sig något direkt.'],
    modes: ['guests'],
    childFriendly: true,
    depth: 'quick',
    minimumParticipants: 2,
  },
  {
    id: 'guest-kitchen-surface',
    roomId: 'kitchen',
    title: 'Frigör kökets blickfång',
    category: 'visible',
    durationMinutes: 8,
    effort: 1,
    tools: ['Korg eller låda', 'Mikrofiberduk'],
    steps: ['Samla lösa saker från köksö och matbord i en korg.', 'Ställ undan synlig disk.', 'Torka av köksö, matbord och den fria delen av bänken.'],
    doneDefinition: ['Köksö och matbord går att använda och de största ytorna är avtorkade.'],
    modes: ['guests'],
    childFriendly: true,
    depth: 'quick',
    minimumParticipants: 2,
  },
  {
    id: 'guest-entrance-floor',
    roomId: 'middle-hall',
    title: 'Ta golvet gästerna möter',
    category: 'floor',
    durationMinutes: 6,
    effort: 1,
    tools: ['Dammsugare'],
    steps: ['Flytta undan skor från gångstråket.', 'Dammsug precis innanför dörren och vägen vidare in.', 'Ställ tillbaka skorna samlat.'],
    doneDefinition: ['Entréns synliga golv och gångstråk är fria från grus, hår och skräp.'],
    modes: ['guests'],
    childFriendly: true,
    depth: 'quick',
    minimumParticipants: 2,
  },
  {
    id: 'guest-final-details',
    roomId: 'living-room',
    title: 'Gör den sista gästkollen',
    category: 'visible',
    durationMinutes: 4,
    effort: 1,
    tools: [],
    steps: ['Tänd den belysning ni vill ha.', 'Kontrollera att det finns en fri plats för jackor, glas och sittande.', 'Gå samma väg som gästen och flytta den sista tydliga saken.'],
    doneDefinition: ['Entré och sällskapsyta fungerar praktiskt när det ringer på.'],
    modes: ['guests'],
    childFriendly: true,
    depth: 'quick',
    minimumParticipants: 2,
    priorityBoost: 0.1,
  },
  {
    id: 'deep-kitchen',
    roomId: 'kitchen',
    title: 'Storstäda köket',
    category: 'hygiene',
    durationMinutes: 55,
    effort: 3,
    tools: ['Mikrofiberdukar', 'Diskborste', 'Dammsugare', 'Mopp'],
    products: ['Diskmedel', 'Hällrengöring', 'Golvrengöring'],
    steps: ['Töm arbetsytorna och ta hand om disken.', 'Rengör luckfronter, handtag, stänkskydd, häll och diskho.', 'Torka bord och fria ytor.', 'Dammsug och våttorka golvet.'],
    doneDefinition: ['Alla arbetsytor är rena och fria.', 'Häll, diskho, fronter och golv är rengjorda.'],
    modes: ['weekend'],
    childFriendly: false,
    depth: 'deep',
    minimumSessionMinutes: 45,
    supersedesTaskIds: ['kitchen-reset', 'kitchen-sink', 'kitchen-hob'],
    includedTaskIds: ['micro-kitchen-towels', 'micro-dishcloth', 'micro-cabinet-fronts', 'micro-cabinet-handles', 'micro-backsplash', 'micro-hood-exterior', 'micro-fridge-handles', 'micro-sink-strainer'],
  },
  {
    id: 'deep-upstairs-bathroom',
    roomId: 'upstairs-bathroom',
    title: 'Gör badrummet uppe skinande rent',
    category: 'hygiene',
    durationMinutes: 45,
    effort: 3,
    tools: ['Mikrofiberdukar', 'Toalettborste', 'Dammsugare', 'Mopp'],
    products: ['Badrumsrengöring'],
    steps: ['Plocka undan och damma uppifrån och ner.', 'Rengör spegel, handfat, kran, dusch eller badkar och toalett.', 'Torka kontaktpunkter och fronter.', 'Dammsug och våttorka golvet; lägg fram rena handdukar.'],
    doneDefinition: ['Sanitetsporslin, spegel och kontaktpunkter är rena.', 'Golvet är rent och torra handdukar ligger framme.'],
    modes: ['weekend'],
    childFriendly: false,
    depth: 'deep',
    minimumSessionMinutes: 45,
    priorityBoost: 1.5,
    repeatGroup: 'bathroom-deep-clean',
    supersedesTaskIds: ['upstairs-bathroom-reset'],
    includedTaskIds: ['micro-bathroom-mirror', 'micro-upstairs-hand-towel', 'micro-soap-dispenser', 'micro-toilet-hinges', 'micro-bathroom-fronts'],
  },
  {
    id: 'deep-downstairs-bathroom',
    roomId: 'downstairs-bathroom',
    title: 'Gör badrummet nere skinande rent',
    category: 'hygiene',
    durationMinutes: 45,
    effort: 3,
    tools: ['Mikrofiberdukar', 'Toalettborste', 'Dammsugare', 'Mopp'],
    products: ['Badrumsrengöring'],
    steps: ['Plocka undan och damma uppifrån och ner.', 'Rengör spegel, handfat, kran, dusch och toalett.', 'Torka kontaktpunkter och fronter.', 'Dammsug och våttorka golvet; lägg fram rena handdukar.'],
    doneDefinition: ['Badrummet är rengjort från högsta yta till golv.', 'Det känns färdigt, inte bara snabbfixat.'],
    modes: ['weekend'],
    childFriendly: false,
    depth: 'deep',
    minimumSessionMinutes: 45,
    repeatGroup: 'bathroom-deep-clean',
    supersedesTaskIds: ['downstairs-bathroom-reset'],
    includedTaskIds: ['micro-downstairs-bathroom-mirror', 'micro-downstairs-hand-towel', 'micro-downstairs-soap-dispenser', 'micro-downstairs-toilet-hinges'],
  },
  {
    id: 'deep-living-room',
    roomId: 'living-room',
    title: 'Storstäda vardagsrummet',
    category: 'floor',
    durationMinutes: 50,
    effort: 3,
    tools: ['Korg eller låda', 'Mikrofiberduk', 'Dammsugare', 'Möbelmunstycke'],
    steps: ['Sortera bort allt som hör hemma i andra rum.', 'Damma fria ytor, lampor och elektronik försiktigt.', 'Dammsug soffor, fåtöljer, lister och hela golvet.', 'Ordna sittplatser och textilier.'],
    doneDefinition: ['Ytor, möbler och golv är rengjorda.', 'Rummet är färdigt att använda utan mer plock.'],
    modes: ['weekend'],
    childFriendly: true,
    depth: 'deep',
    minimumSessionMinutes: 45,
    supersedesTaskIds: ['living-room-reset', 'living-room-floor'],
  },
  {
    id: 'deep-laundry-room',
    roomId: 'laundry-room',
    title: 'Återställ hela tvättstugan',
    category: 'laundry',
    durationMinutes: 45,
    effort: 3,
    tools: ['Tvättkorgar', 'Mikrofiberduk', 'Dammsugare', 'Mopp'],
    steps: ['Flytta all ren tvätt till rätt person och starta nästa tydliga omgång.', 'Frigör och rengör arbetsbänken.', 'Torka maskinernas utsidor och rensa löst skräp.', 'Dammsug och våttorka golvet.'],
    doneDefinition: ['Ren tvätt har lämnat rummet och nästa omgång är tydlig.', 'Bänk, maskiner och golv är rengjorda.'],
    modes: ['weekend'],
    childFriendly: true,
    depth: 'deep',
    minimumSessionMinutes: 45,
    supersedesTaskIds: ['laundry-next-step', 'laundry-bench-reset'],
  },
  {
    id: 'deep-house-floors',
    roomId: 'basement-hall',
    title: 'Ta husets golv och trappor',
    category: 'floor',
    durationMinutes: 55,
    effort: 3,
    tools: ['Korg eller låda', 'Dammsugare', 'Mopp'],
    steps: ['Samla lösa saker från gångstråk och trappor i en korg.', 'Dammsug hallar, trappor och fria sovrumsgolv uppifrån och ner.', 'Våttorka tåliga golv där det behövs.', 'Ställ tillbaka sådant som flyttats.'],
    doneDefinition: ['Husets huvudsakliga gångstråk, trappor och fria golvytor är rengjorda.'],
    modes: ['weekend'],
    childFriendly: true,
    depth: 'deep',
    minimumSessionMinutes: 45,
    supersedesTaskIds: ['upstairs-hall-floor', 'bedroom-floor'],
  },
  {
    id: 'kitchen-reset',
    roomId: 'kitchen',
    title: 'Återställ köket',
    category: 'visible',
    durationMinutes: 8,
    effort: 1,
    tools: ['Korg eller låda', 'Mikrofiberduk'],
    steps: ['Samla saker som hör hemma någon annanstans.', 'Plocka undan disk och frigör köksön.', 'Torka köksö, skänkar och matbord.'],
    doneDefinition: ['Köksön och matbordet är användbara.', 'Det synliga köksplocket är borta.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'kitchen-sink',
    roomId: 'kitchen',
    title: 'Gör diskhon fräsch',
    category: 'hygiene',
    durationMinutes: 5,
    effort: 1,
    tools: ['Diskborste', 'Mikrofiberduk'],
    products: ['Diskmedel'],
    steps: ['Töm silen och skölj ur hon.', 'Rengör runt kran och avlopp med diskmedel.', 'Skölj och torka torrt.'],
    doneDefinition: ['Diskhon är fri från matrester.', 'Kran och kanter är torra utan synliga fläckar.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'kitchen-hob',
    roomId: 'kitchen',
    title: 'Putsa glashällen',
    category: 'hygiene',
    durationMinutes: 7,
    effort: 2,
    tools: ['Hällskrapa', 'Mikrofiberduk'],
    products: ['Hällrengöring'],
    steps: ['Kontrollera att hällen är helt sval.', 'Ta bort lös smuts och använd skrapan i låg vinkel vid behov.', 'Applicera hällrengöring och torka sedan fuktigt och torrt.'],
    doneDefinition: ['Hällen är fri från matrester och ränder.'],
    modes: ['weekend'],
    childFriendly: false,
    safetyWarnings: ['Använd aldrig skrapan på varm häll.', 'Använd endast avsett hällmedel.'],
  },
  {
    id: 'middle-hall-reset',
    roomId: 'middle-hall',
    title: 'Frigör hallen',
    category: 'visible',
    durationMinutes: 6,
    effort: 1,
    tools: ['Korg eller låda'],
    steps: ['Häng upp ytterkläder som ligger framme.', 'Ställ skor parvis eller i skohyllan.', 'Lägg småsaker från skänken i rätt rum.'],
    doneDefinition: ['Det finns en tydlig väg genom hallen.', 'Hatthylla och skohylla känns hanterbara.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'upstairs-hall-floor',
    roomId: 'upstairs-hall',
    title: 'Frigör övre hallgolvet',
    category: 'floor',
    durationMinutes: 6,
    effort: 1,
    tools: ['Korg eller låda', 'Dammsugare'],
    steps: ['Lägg kläder, handdukar och sängkläder i rätt garderob eller korg.', 'Samla sådant som hör hemma i ett sovrum.', 'Dammsug den fria golvytan och trappans översta steg.'],
    doneDefinition: ['Det går att gå genom hallen utan att kliva över saker.', 'Det synliga hallplocket är samlat.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'living-room-reset',
    roomId: 'living-room',
    title: 'Landa vardagsrummet',
    category: 'visible',
    durationMinutes: 8,
    effort: 1,
    tools: ['Korg eller låda', 'Filtborste'],
    steps: ['Samla saker som hör hemma på annat håll.', 'Vik filtar och ordna soffan.', 'Frigör tv-bänk och matbord från småsaker.'],
    doneDefinition: ['Sittplatserna går att använda direkt.', 'De stora ytorna känns lugna.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'living-room-floor',
    roomId: 'living-room',
    title: 'Fånga smulor och hår',
    category: 'floor',
    durationMinutes: 10,
    effort: 2,
    tools: ['Dammsugare', 'Munstycke för möbler'],
    steps: ['Flytta undan det som står i gångstråken.', 'Dammsug synliga golvytor och runt soffan.', 'Ta smulor och hår från fåtöljer och soffans synliga delar.'],
    doneDefinition: ['Golvets gångstråk är fria från smulor och hår.'],
    modes: ['daily', 'weekend', 'guests'],
    childFriendly: true,
  },
  {
    id: 'take-out-trash',
    roomId: 'kitchen',
    title: 'Ta ut soporna',
    category: 'odor',
    durationMinutes: 3,
    effort: 1,
    tools: ['Ny soppåse'],
    steps: ['Knyt ihop matavfall och andra fulla påsar.', 'Torka snabbt av kanten om den är kladdig.', 'Sätt i en ny påse och ta ut soporna.'],
    doneDefinition: ['Ingen full soppåse står synlig i köket eller hallen.'],
    modes: ['daily', 'weekend', 'guests'],
    childFriendly: true,
  },
  {
    id: 'air-common-rooms',
    roomId: 'living-room',
    title: 'Vädra gemensamma ytor',
    category: 'odor',
    durationMinutes: 3,
    effort: 1,
    tools: [],
    steps: ['Öppna ett eller två fönster en kort stund om vädret tillåter.', 'Stäng efter några minuter så huset inte kyls ner.', 'Kontrollera att ingen tydlig luktkälla finns kvar.'],
    doneDefinition: ['Kök och vardagsrum känns luftiga utan att parfym behövs.'],
    modes: ['daily', 'guests'],
    childFriendly: true,
    seasonalVariants: {
      winter: {
        durationMinutes: 3,
        steps: ['Tvärvädra intensivt i ungefär 2–4 minuter.', 'Stäng innan väggar och möbler hinner kylas ner.', 'Kontrollera att ingen tydlig luktkälla finns kvar.'],
      },
      spring: {
        durationMinutes: 6,
        steps: ['Öppna ett par fönster i ungefär 5–10 minuter om vädret tillåter.', 'Skapa gärna korsdrag och stäng när luften känns utbytt.', 'Kontrollera att ingen tydlig luktkälla finns kvar.'],
      },
      summer: {
        durationMinutes: 10,
        steps: ['Vädra gemensamma ytor i ungefär 10–20 minuter när uteluften är lämplig.', 'Skapa korsdrag utan att lämna fönster osäkra eller obevakade.', 'Kontrollera att ingen tydlig luktkälla finns kvar.'],
      },
      autumn: {
        durationMinutes: 6,
        steps: ['Öppna ett par fönster i ungefär 5–10 minuter om vädret tillåter.', 'Skapa gärna korsdrag och stäng innan huset kyls ner.', 'Kontrollera att ingen tydlig luktkälla finns kvar.'],
      },
    },
  },
  {
    id: 'upstairs-bathroom-reset',
    roomId: 'upstairs-bathroom',
    title: 'Snabbreset badrum uppe',
    category: 'hygiene',
    durationMinutes: 10,
    effort: 2,
    tools: ['Mikrofiberduk', 'Toalettborste'],
    products: ['Badrumsrengöring'],
    steps: ['Plocka undan produkter från handfatet.', 'Rengör handfat, kran och toalettens utsida.', 'Byt handduk och torka spegelns synliga märken.'],
    doneDefinition: ['Handfatet är rent och torrt.', 'Det finns en fräsch handduk framme.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
    includedTaskIds: ['micro-bathroom-mirror', 'micro-upstairs-hand-towel'],
  },
  {
    id: 'downstairs-bathroom-reset',
    roomId: 'downstairs-bathroom',
    title: 'Snabbreset badrum nere',
    category: 'hygiene',
    durationMinutes: 10,
    effort: 2,
    tools: ['Mikrofiberduk', 'Toalettborste'],
    products: ['Badrumsrengöring'],
    steps: ['Plocka undan produkter från handfatet.', 'Rengör handfat, kran och toalettens utsida.', 'Byt handduk och torka spegelns synliga märken.'],
    doneDefinition: ['Handfatet är rent och torrt.', 'Det finns en fräsch handduk framme.'],
    modes: ['weekend'],
    childFriendly: true,
    includedTaskIds: ['micro-downstairs-bathroom-mirror', 'micro-downstairs-hand-towel'],
  },
  {
    id: 'upstairs-bedroom-reset',
    roomId: 'bedroom-1',
    title: 'Gör ett sovrum sovklart',
    category: 'visible',
    durationMinutes: 8,
    effort: 1,
    tools: ['Korg eller låda'],
    steps: ['Lägg smutstvätt i tvättkorgen.', 'Samla saker från golv och skrivbord.', 'Bädda sängen och öppna fönstret en kort stund om vädret tillåter.'],
    doneDefinition: ['Sängen är bäddad.', 'Golvet och skrivbordet går att använda.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'bedroom-floor',
    roomId: 'bedroom-1',
    title: 'Frigör sovrumsgolvet',
    category: 'floor',
    durationMinutes: 8,
    effort: 1,
    tools: ['Korg eller låda', 'Dammsugare'],
    steps: ['Lägg kläder i rätt korg eller garderob.', 'Samla saker från golvet till rätt rum.', 'Dammsug den fria golvytan.'],
    doneDefinition: ['Det går att gå genom rummet utan att flytta saker.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'laundry-next-step',
    roomId: 'laundry-room',
    title: 'Ta tvätten ett steg framåt',
    category: 'laundry',
    durationMinutes: 8,
    effort: 1,
    tools: ['Tvättkorg'],
    steps: ['Välj den rena omgång som väntat längst.', 'Vik eller personsortera direkt i korgar.', 'Ställ korgarna där rätt person kan ta nästa steg.'],
    doneDefinition: ['En ren omgång har lämnat tvättstugan.', 'Nästa ägare är tydlig.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'laundry-bench-reset',
    roomId: 'laundry-room',
    title: 'Frigör tvättbänken',
    category: 'visible',
    durationMinutes: 6,
    effort: 1,
    tools: ['Korg eller låda', 'Mikrofiberduk'],
    steps: ['Samla tomma förpackningar och småsaker.', 'Flytta undan torr tvätt till rätt korg.', 'Torka en fri arbetsyta på bänken.'],
    doneDefinition: ['Det finns en fri yta att sortera på.', 'Torkställningen står inte i vägen mer än nödvändigt.'],
    modes: ['daily', 'weekend'],
    childFriendly: true,
  },
  {
    id: 'basement-lounge-reset',
    roomId: 'basement-lounge',
    title: 'Väck gillestugan',
    category: 'visible',
    durationMinutes: 10,
    effort: 1,
    tools: ['Korg eller låda', 'Mikrofiberduk'],
    steps: ['Samla glas, papper och saker som hör hemma på annat håll.', 'Ordna sittplatserna runt tv:n.', 'Torka de synliga ytorna runt soffan och tv-bänken.'],
    doneDefinition: ['Rummet går att använda utan första insats.', 'Sittplatser och tv-bänk känns inbjudande.'],
    modes: ['weekend'],
    childFriendly: true,
  },
  {
    id: 'cinema-office-rescue',
    roomId: 'cinema-office',
    title: 'Ge biorummet en chans',
    category: 'visible',
    durationMinutes: 12,
    effort: 2,
    tools: ['Korg eller låda', 'Mikrofiberduk', 'Kabelband'],
    steps: ['Samla sådant som inte hör till rummet i en tillfällig låda.', 'Frigör en tydlig arbetsyta på skrivbordet.', 'Ordna skärmar och lösa sladdar utan att koppla ur något viktigt.', 'Rätta till soffan och fåtöljerna.'],
    doneDefinition: ['Det finns en fri plats att sitta och en fri plats att arbeta.', 'Lösa saker är samlade, inte utspridda.'],
    modes: ['weekend'],
    childFriendly: false,
    safetyWarnings: ['Flytta inte kablar eller elektronik som är i bruk.'],
  },
  {
    id: 'odor-source-check',
    roomId: 'kitchen',
    title: 'Luktkoll utan parfym',
    category: 'odor',
    durationMinutes: 5,
    effort: 1,
    tools: ['Ny disktrasa'],
    products: ['Diskmedel'],
    steps: ['Töm matavfall och kontrollera sopkärlet.', 'Byt disktrasa och skölj ur diskhon.', 'Vädra kort och kontrollera om lukten finns kvar.'],
    doneDefinition: ['Den tydligaste luktkällan är åtgärdad eller noterad.'],
    modes: ['daily', 'guests'],
    childFriendly: true,
    seasonalVariants: {
      winter: { steps: ['Töm matavfall och kontrollera sopkärlet.', 'Byt disktrasa och skölj ur diskhon.', 'Tvärvädra i ungefär 2–4 minuter och kontrollera om lukten finns kvar.'] },
      summer: { steps: ['Töm matavfall och kontrollera sopkärlet.', 'Byt disktrasa och skölj ur diskhon.', 'Vädra i ungefär 10–20 minuter när uteluften är lämplig och kontrollera om lukten finns kvar.'] },
    },
  },
  {
    id: 'terrace-reset',
    roomId: 'terrace',
    title: 'Gör altanen brukbar',
    category: 'visible',
    durationMinutes: 12,
    effort: 2,
    tools: ['Sopborste', 'Korg eller låda'],
    steps: ['Samla glas, filtar och saker som ska in.', 'Sopa de ytor där man går och sitter.', 'Ordna sittplatserna så att de går att använda direkt.'],
    doneDefinition: ['Det går att gå ut och sätta sig utan att börja städa först.'],
    modes: ['weekend'],
    childFriendly: true,
  },
];
